/**
 * PlaceCard — editorial list row. Per UX §3.3.
 *
 * Per the assignment for /places: card NOT dashboard table — place name (serif
 * h4-equivalent), category eyebrow above name, status pill, location subtitle
 * if address exists, recent visit summary, hairline divider between cards.
 *
 * Clicking the card navigates to the detail page via Link.
 */
import { Link } from 'react-router-dom';
import type { PlaceSummary, Category } from '@shared/types';
import { StatusPill } from './ui/StatusPill';
import { DotRating } from './ui/DotRating';
import { formatRelative } from '../lib/date';
import { cn } from '../lib/cn';

export interface PlaceCardProps {
  place: PlaceSummary;
  category?: Pick<Category, 'name'> | null;
  className?: string;
}

export function PlaceCard({ place, category, className }: PlaceCardProps) {
  const rating =
    place.average_rating !== null
      ? (Math.round(place.average_rating) as 0 | 1 | 2 | 3 | 4 | 5)
      : 0;

  return (
    <article
      className={cn(
        'group flex items-start gap-6',
        'py-6 border-b border-ink-200 last:border-b-0',
        className,
      )}
      data-testid="place-card"
    >
      <div className="flex-1 min-w-0">
        <p className="font-sans text-[0.65rem] uppercase tracking-[0.18em] text-ink-500 mb-1.5">
          {category?.name ?? 'Place'}
        </p>
        <h3 className="font-serif text-2xl md:text-3xl text-ink-900 leading-tight">
          <Link
            to={`/places/${place.id}`}
            className="hover:text-ink-700 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-[3px] focus-visible:outline-accent"
          >
            {place.name}
          </Link>
        </h3>
        <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-2">
          <StatusPill status={place.status} />
          {place.last_visited_at ? (
            <span className="text-xs text-ink-500">
              Last visited {formatRelative(place.last_visited_at)}
            </span>
          ) : (
            <span className="text-xs italic text-ink-500">
              No journal entries yet
            </span>
          )}
          {rating > 0 ? (
            <span className="inline-flex items-center gap-2">
              <DotRating value={rating} size="sm" readOnly />
            </span>
          ) : null}
        </div>
      </div>
    </article>
  );
}
